import {Alert} from 'src/design/atoms/Alert';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React, {useState} from 'react';
import type {InspectionScopeDeviceResponse} from 'src/api/ProactApiClient';
import {FullScreenModal} from 'src/design/molecules/FullScreenModal';
import {QrReader} from 'src/design/organisms/QrReader';

export interface DeviceScanModalProps {
  opened: boolean;
  /** Full inspection scope — decoded text is matched here, not the filtered table. */
  devices: InspectionScopeDeviceResponse[];
  onClose: () => void;
  onOpenDevice: (device: InspectionScopeDeviceResponse) => void;
  onSearchFallback: (text: string) => void;
}

// QR-only this cycle (1D barcode deferred). Decoded text matches by exact serial: one hit
// opens the entry; none/ambiguous falls back to the device table with the text prefilled.
export const DeviceScanModal: React.FC<DeviceScanModalProps> = ({
  opened,
  devices,
  onClose,
  onOpenDevice,
  onSearchFallback,
}) => {
  const [notice, setNotice] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);

  const handleClose = () => {
    setNotice(null);
    setCameraError(null);
    onClose();
  };

  const handleScanned = (decoded: string) => {
    const trimmed = decoded.trim();
    const matches = trimmed
      ? devices.filter(device => !!device.serialNumber && device.serialNumber === trimmed)
      : [];

    if (trimmed && matches.length === 1) {
      handleClose();
      onOpenDevice(matches[0]);
      return;
    }

    if (trimmed && matches.length === 0) {
      setNotice("Not in this inspection's scope");
      return;
    }

    handleClose();
    onSearchFallback(trimmed);
  };

  return (
    <FullScreenModal
      title="Scan Device"
      open={opened}
      onCloseCallback={handleClose}>
      <Stack gap="md">
        {notice && (
          <Alert color="yellow" title={notice}>
            Scan again, or close to search manually.
          </Alert>
        )}
        <QrReader onScanned={handleScanned} onError={setCameraError} />
        {cameraError && (
          <Text size="sm" c="red">
            {cameraError}
          </Text>
        )}
      </Stack>
    </FullScreenModal>
  );
};
